<script lang="ts" setup>
import { ref, computed, onUnmounted } from 'vue'
import { useDialogPluginComponent } from 'quasar'
import type { VendorEvent } from '@/utils/vendors/types'
import { formatFileSize, formatDateTime } from '@/utils/format'

const props = defineProps<{
  event: VendorEvent
  /** Pre-computed out-of-bounds warning text, if any. Shown as a dismissible banner. */
  outOfBoundsWarning?: string
  /** Called when the user confirms. Dialog stays open and shows loading until this resolves. */
  applyFiles: (files: File[]) => Promise<void>
  /** Called when the user chooses "skip — enter manually" from the warning banner. */
  skipToManual?: () => void
}>()

defineEmits([...useDialogPluginComponent.emits])

const { dialogRef, onDialogHide, onDialogOK, onDialogCancel } = useDialogPluginComponent()

// ── Clip-time parsing ──────────────────────────────────────────────────────────

function parseClipStartTime(filename: string): Date | null {
  const m = filename.match(/^(\d{4})-(\d{2})-(\d{2})_(\d{2})-(\d{2})-(\d{2})-/)
  if (!m) return null
  return new Date(`${m[1]}-${m[2]}-${m[3]}T${m[4]}:${m[5]}:${m[6]}`)
}

function clipTimeLabel(filename: string): string | null {
  const m = filename.match(/^\d{4}-\d{2}-\d{2}_(\d{2})-(\d{2})-(\d{2})-/)
  if (!m) return null
  return `${m[1]}:${m[2]}:${m[3]}`
}

function cameraLabel(filename: string): string {
  if (filename === 'event.mp4') return 'Event (Combined)'
  const m = filename.match(/-([a-z_]+)\.mp4$/i)
  return m ? m[1].replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()) : filename
}

// ── File groups ────────────────────────────────────────────────────────────────

interface FileEntry {
  file: File
  index: number
}

interface FileGroup {
  key: string
  timeLabel: string
  startTime: Date | null
  nearEvent: boolean
  files: FileEntry[]
}

const PRE_SELECT_BEFORE_MS = 90_000
const PRE_SELECT_AFTER_MS = 30_000

const eventTime = props.event.dateTime?.getTime() ?? null

function isNearEvent(startTime: Date | null): boolean {
  if (eventTime === null || startTime === null) return true
  const t = startTime.getTime()
  return t >= eventTime - PRE_SELECT_BEFORE_MS && t <= eventTime + PRE_SELECT_AFTER_MS
}

const fileGroups = computed<FileGroup[]>(() => {
  const groupMap = new Map<string, FileGroup>()

  props.event.filesToUpload.forEach((file, index) => {
    const keyMatch = file.name.match(/^(\d{4}-\d{2}-\d{2}_\d{2}-\d{2}-\d{2})-/)
    const key = keyMatch ? keyMatch[1] : '__ungrouped__'
    const tLabel = clipTimeLabel(file.name) ?? file.name

    if (!groupMap.has(key)) {
      const startTime = parseClipStartTime(file.name)
      groupMap.set(key, {
        key,
        timeLabel: tLabel,
        startTime,
        nearEvent: isNearEvent(parseClipStartTime(file.name)),
        files: []
      })
    }
    groupMap.get(key)!.files.push({ file, index })
  })

  return [...groupMap.values()]
    .sort((a, b) => (b.startTime?.getTime() ?? 0) - (a.startTime?.getTime() ?? 0))
    .map((g) => ({
      ...g,
      files: [...g.files].sort((a, b) => a.file.name.localeCompare(b.file.name))
    }))
})

const hasSmartSelection = computed(
  () => eventTime !== null && fileGroups.value.some((g) => !g.nearEvent)
)

// ── Selection state ────────────────────────────────────────────────────────────

const selected = ref<boolean[]>(
  props.event.filesToUpload.map((file) => isNearEvent(parseClipStartTime(file.name)))
)

const selectedCount = computed(() => selected.value.filter(Boolean).length)
const allSelected = computed(() => selected.value.every(Boolean))

function toggleAll() {
  const next = !allSelected.value
  selected.value = selected.value.map(() => next)
}

// ── Video preview ──────────────────────────────────────────────────────────────

const previewUrls = new Map<number, string>()
const previewIndex = ref<number | null>(null)

const previewUrl = computed(() =>
  previewIndex.value !== null ? (previewUrls.get(previewIndex.value) ?? null) : null
)

function setPreview(index: number) {
  if (isProcessing.value) return
  if (!previewUrls.has(index)) {
    previewUrls.set(index, URL.createObjectURL(props.event.filesToUpload[index]))
  }
  previewIndex.value = index
}

onUnmounted(() => {
  previewUrls.forEach((url) => URL.revokeObjectURL(url))
})

// ── Summary ────────────────────────────────────────────────────────────────────

const eventSummary = computed(() => {
  const meta = props.event.rawMeta as any
  const place = meta?.place ?? null
  const parts = [props.event.dateTime ? formatDateTime(props.event.dateTime) : null, place].filter(
    Boolean
  )
  return parts.join(' · ')
})

// ── Confirm / loading ──────────────────────────────────────────────────────────

const isProcessing = ref(false)

async function onConfirm() {
  const files = props.event.filesToUpload.filter((_, i) => selected.value[i])
  isProcessing.value = true
  try {
    await props.applyFiles(files)
    onDialogOK(files)
  } catch {
    // Leave dialog open so the user can retry or cancel
    isProcessing.value = false
  }
}

function onSkipToManual() {
  props.skipToManual?.()
  onDialogCancel()
}
</script>

<template>
  <q-dialog ref="dialogRef" :persistent="isProcessing" @hide="onDialogHide">
    <q-card style="width: 560px; max-width: 95vw" class="column no-wrap">
      <!-- Header -->
      <q-card-section class="row items-start no-wrap q-pb-xs">
        <div class="col">
          <div class="text-h6 text-blue-grey-9">Select footage to upload</div>
          <div v-if="eventSummary" class="text-caption text-blue-grey-6 q-mt-xs">
            {{ eventSummary }}
          </div>
        </div>
        <q-btn
          flat
          round
          dense
          icon="mdi-close"
          aria-label="Close"
          color="blue-grey-5"
          :disable="isProcessing"
          @click="onDialogCancel"
        />
      </q-card-section>

      <q-separator />

      <!-- Video preview -->
      <div class="preview-container bg-black">
        <video
          v-if="previewUrl"
          :key="previewUrl"
          :src="previewUrl"
          controls
          autoplay
          class="preview-video"
        />
        <div
          v-else
          class="preview-placeholder row items-center justify-center full-width full-height"
        >
          <div class="text-center text-blue-grey-6">
            <q-icon name="mdi-play-circle-outline" size="2.5em" />
            <div class="text-caption q-mt-xs">Click a file to preview</div>
          </div>
        </div>
      </div>

      <q-separator />

      <!-- Out-of-bounds warning banner -->
      <div v-if="outOfBoundsWarning" class="q-px-md q-pt-sm q-pb-xs">
        <q-banner
          dense
          rounded
          class="bg-orange-1 text-orange-10"
          style="border: 1px solid #ffe082"
        >
          <template v-slot:avatar>
            <q-icon name="mdi-alert" color="orange-8" />
          </template>
          <div class="text-body2">{{ outOfBoundsWarning }}</div>
          <div class="text-caption q-mt-xs">
            You can use these values anyway, or skip and enter details manually.
          </div>
          <template v-slot:action>
            <q-btn
              v-if="skipToManual"
              flat
              dense
              size="sm"
              label="Skip — enter manually"
              color="orange-9"
              :disable="isProcessing"
              @click="onSkipToManual"
            />
          </template>
        </q-banner>
      </div>

      <!-- Smart-selection hint -->
      <div v-if="hasSmartSelection" class="q-px-md q-pt-sm q-pb-none">
        <span class="text-caption text-blue-grey-6">
          Clips near the event time are pre-selected. Use "Select all" to include earlier footage.
        </span>
      </div>

      <!-- File list grouped by clip time -->
      <q-scroll-area style="height: 340px" class="col-shrink-0">
        <q-list role="list" separator>
          <template v-for="group in fileGroups" :key="group.key">
            <q-item-label
              header
              class="row items-center no-wrap q-py-xs q-px-md bg-grey-1"
              style="min-height: 0"
            >
              <span class="text-caption text-weight-bold text-blue-grey-7 q-mr-sm">
                {{ group.timeLabel }}
              </span>
              <q-chip
                v-if="group.nearEvent"
                dense
                outline
                color="blue-8"
                text-color="blue-8"
                icon="mdi-clock-alert-outline"
                label="Near event"
                class="q-ma-none"
                style="font-size: 10px; height: 20px"
              />
            </q-item-label>

            <q-item
              v-for="entry in group.files"
              :key="entry.index"
              clickable
              dense
              :active="previewIndex === entry.index"
              active-class="bg-blue-1"
              class="q-pl-lg"
              style="min-height: 36px"
              @click="setPreview(entry.index)"
            >
              <q-item-section side>
                <q-checkbox
                  v-model="selected[entry.index]"
                  color="blue-8"
                  dense
                  :disable="isProcessing"
                  @click.stop
                />
              </q-item-section>

              <q-item-section avatar style="min-width: 28px">
                <q-icon
                  name="mdi-video"
                  :color="previewIndex === entry.index ? 'blue-8' : 'blue-grey-4'"
                  size="xs"
                />
              </q-item-section>

              <q-item-section>
                <q-item-label class="text-weight-medium text-blue-grey-9" style="font-size: 13px">
                  {{ cameraLabel(entry.file.name) }}
                </q-item-label>
                <q-item-label caption style="font-size: 11px">
                  {{ formatFileSize(entry.file.size) }}
                </q-item-label>
              </q-item-section>

              <q-item-section side>
                <q-icon
                  v-if="previewIndex === entry.index"
                  name="mdi-play-circle"
                  color="blue-8"
                  size="xs"
                />
              </q-item-section>
            </q-item>
          </template>
        </q-list>
      </q-scroll-area>

      <q-separator />

      <!-- Actions -->
      <q-card-actions class="q-px-md q-py-sm">
        <q-btn
          flat
          dense
          size="sm"
          :label="allSelected ? 'Select none' : 'Select all'"
          color="blue-grey-7"
          :disable="isProcessing"
          @click="toggleAll"
        />
        <q-space />
        <q-btn
          flat
          label="Cancel"
          color="blue-grey-7"
          :disable="isProcessing"
          @click="onDialogCancel"
        />
        <q-btn
          unelevated
          color="blue-8"
          :label="
            isProcessing
              ? 'Preparing…'
              : `Use ${selectedCount} file${selectedCount !== 1 ? 's' : ''}`
          "
          :loading="isProcessing"
          :disable="selectedCount === 0 || isProcessing"
          class="q-ml-sm"
          @click="onConfirm"
        />
      </q-card-actions>

      <!-- Full-card loading overlay -->
      <q-inner-loading :showing="isProcessing" color="blue-8">
        <q-spinner-dots size="48px" color="blue-8" />
        <div class="text-caption text-blue-grey-7 q-mt-sm">Preparing upload…</div>
      </q-inner-loading>
    </q-card>
  </q-dialog>
</template>

<style scoped>
.preview-container {
  height: 220px;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.preview-video {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.preview-placeholder {
  width: 100%;
  height: 100%;
}
</style>
